/**
 * Stories for Golden Learn
 *
 * Story-led learning for Levels 1-5 (to start).
 * Each story shows WHY a concept matters in real life.
 *
 * Structure:
 * - content: 100-150 words, third person, conversational
 * - context: optional attribution or grounding
 * - teachBackPrompt: specific reflection question
 *
 * IMPORTANT: Stories must be written with lived context.
 * Do NOT use placeholder text or generic examples.
 * Leave content empty until real stories are provided.
 */

export const stories = {
  // Level 1: Hello World / print()
  1: {
    content: `Every morning, before customers arrive, Thandi stands outside her spaza shop with a piece of chalk.

She writes one line on the small board by the door:
"Bread today: R14."

She doesn't rebuild the shop. She doesn't explain herself to every passerby.
She simply writes the message once, and anyone who walks past can see it.

When the price changes, she erases the old number and writes a new one.
The message changes, but the board stays the same.

In programming, the print() function does something similar.
It lets you place a message where the computer can show it to the world.

Today, you'll learn how to make the computer speak clearly — by printing your first message.`,
    context: null,
    reflectionPrompt: "Where in your life do you write or say something once so others can see or hear it? What did the computer do exactly as instructed when you used print()?",
    teachBackPrompt: "How would you explain the print() function to someone younger than you, using an example from your own life? Why does this kind of thinking matter—being able to make a computer speak clearly?"
  },

  // Level 2: Variables
  2: {
    content: `Every week, Sipho's mother keeps grocery money in a tin on the kitchen shelf.

The tin is always in the same place. It's always called "grocery money."
But what's inside changes.

Monday: R300.
Wednesday: R150 after buying bread and milk.
Friday: R200 after she adds more from her pay.

The tin doesn't change. The name doesn't change. Only the amount inside.

She doesn't need a new tin every time the money changes.
She just updates what's inside the same container.

In programming, a variable works the same way.
It has a name that stays constant, but the value it holds can change.

Today, you'll learn how to create containers that remember information — and how to update them when things change.`,
    context: null,
    reflectionPrompt: "Think of something you keep track of that changes regularly. How do you remember the current amount or status? What did the computer do exactly as instructed when it stored and updated your variable?",
    teachBackPrompt: "Explain variables to someone younger than you. Why is it useful to store information that can change? Why does this kind of thinking matter—understanding that computers remember exactly what you tell them?"
  },

  // Level 3: Data Types (strings, numbers, booleans)
  3: {
    content: `At the market, Nomsa keeps a notebook for her vegetable stall.

For each item, she writes three things:
- The name: "Tomatoes"
- The price: R25
- Whether she has stock: Yes or No

She can't add "Tomatoes" and "R25" together — one is a word, one is a number.
She can't multiply "Yes" by 3 — it's an answer, not an amount.

Each type of information works differently.

Numbers can be calculated.
Words can be read and written.
Yes/No answers help her make decisions.

The computer needs to know what type of information it's working with, just like Nomsa needs to know whether she's writing a name, counting money, or checking if something is in stock.

In programming, these are called data types.

Today, you'll learn about strings (words), integers (whole numbers), and booleans (yes/no) — and why it matters which one you're using.`,
    context: null,
    reflectionPrompt: "When you write down information, how do you know whether to use words or numbers? Give an example from today. What did the computer do exactly as instructed when you gave it different types of data?",
    teachBackPrompt: "Explain to someone younger why the computer needs to know if something is a number, a word, or a yes/no answer. Use an example from your life. Why does this kind of thinking matter—recognizing that different information needs different handling?"
  },

  // Level 4: Input and Output
  4: {
    content: `Every morning at the taxi rank, the same exchange happens dozens of times.

A person approaches the driver.
"How much to town?"

The driver answers.
"R15."

The person decides whether to climb in.

This is how information moves: someone asks, someone responds, someone acts on the answer.

If the driver didn't answer, the person couldn't decide.
If the person didn't ask, the driver wouldn't know what they needed.

Programs work the same way.
They ask questions (input), you answer, and they use your answer to decide what to do next.
They also give you information (output) so you can make your own decisions.

Today, you'll learn how to make programs that listen for answers and speak when they have something to say.`,
    context: null,
    reflectionPrompt: "Think of a time today when you asked someone a question before making a decision. What did you need to know? What did the computer do exactly as instructed when it asked for input and gave output?",
    teachBackPrompt: "Explain to someone younger how a program asks for information and gives information back. Use an example from daily life. Why does this kind of thinking matter—understanding two-way conversation with machines?"
  },

  // Level 5: Basic Math Operations
  5: {
    content: `Four friends share a taxi home from work. The fare is R60.

They need to split it equally: R60 ÷ 4 = R15 each.

If someone pays R10 and someone else pays R20, it's not fair.
The math has to be right, or trust breaks.

Later, one friend buys bread for R12 and milk for R18.
R12 + R18 = R30 total.
She hands the shopkeeper R50.
The change should be R20.

If the shopkeeper gives back R15, she notices immediately.
The calculation protects her.

People do math every day — not for school, but to make sure things are fair and correct.

Computers can do the same calculations, but faster and without mistakes.

Today, you'll learn how to use Python to add, subtract, multiply, and divide — the same operations you already use to survive.`,
    context: null,
    reflectionPrompt: "When did you last need to calculate something to make sure you weren't being cheated or to split costs fairly? Where could this help you save time, money, or mistakes?",
    teachBackPrompt: "Explain to someone younger why computers are good at math, and give an example of when you need to calculate something in daily life. Why does this kind of thinking matter—using calculation to ensure fairness and accuracy?"
  },

  // Level 6: Conditionals (if/else)
  6: {
    content: `Bongani sells fruit at the corner of the main road.

Every morning, he looks at the sky.

If it's clear, he sets up his full display with mangoes and bananas spread out.
If it's cloudy and looks like rain, he keeps half his stock in boxes, protected.

The sky decides his setup. He doesn't guess—he looks and chooses.

One action when true. Different action when false.

Programs make the same kind of choices every day.
If the password is correct, log in. Otherwise, show an error.
If there's stock, sell the item. Otherwise, say "sold out."

In programming, this is called a conditional—a decision based on whether something is true or false.

Today, you'll learn how to make programs that look at conditions and choose what to do next.`,
    context: null,
    reflectionPrompt: "When did you last change your plan because something was true or false (like rain, or someone arriving late)? Where else in your life do you look at conditions and choose actions based on what's true?",
    teachBackPrompt: "Explain to someone younger when a program needs to make a choice based on a condition. Use an example from daily life. Why does this kind of thinking matter—responding to reality rather than guessing?"
  },

  // Level 7: Multiple Conditions (elif)
  7: {
    content: `Jabu drives a taxi between town and the township.

The fare depends on where passengers are going:
- To the taxi rank: R8
- To the market: R12
- All the way to the hospital: R20

He doesn't ask yes or no. He asks "where to?" and the answer determines the price.

Three different destinations. Three different fares. One decision process.

Programs work the same way when there are multiple distinct options.
Not just yes or no, but: option A, option B, or option C.

In programming, this uses "elif"—else if.
It means: if not the first thing, check the second. If not that, check the third.

Today, you'll learn how to make programs that choose between multiple possibilities.`,
    context: null,
    reflectionPrompt: "Think of a decision where you had three or more different options to choose from. How did you decide? Where else in your life do you handle situations where different cases need different responses?",
    teachBackPrompt: "Explain to someone how a program chooses between multiple possibilities, not just yes or no. Why does this kind of thinking matter—handling complexity by breaking it into distinct cases?"
  },

  // Level 8: Boolean Logic (and, or, not)
  8: {
    content: `Mrs. Dlamini runs a small shop that offers credit to regular customers.

But not to everyone. To get credit, a customer must meet two requirements:
They must have an ID, AND they must have a good payment history.

If they have ID but bad history: no credit.
If they have good history but no ID: no credit.
Both must be true.

Sometimes she accepts mobile money OR cash. Either one works.

Programs make decisions the same way, combining conditions.
"Both must be true" is AND.
"Either one works" is OR.
"The opposite" is NOT.

In programming, these are called boolean operators—ways to combine true/false conditions.

Today, you'll learn how to make programs that combine conditions to make smarter decisions.`,
    context: null,
    reflectionPrompt: "When have you needed two things to both be true before proceeding? Or when was either option acceptable? Where else in your life do you combine conditions to make better decisions?",
    teachBackPrompt: "Explain to someone how you combine conditions when making decisions. Use an example where both things must be true, or where either option works. Why does this kind of thinking matter—combining conditions protects you from incomplete information?"
  },

  // Level 9: Comparison Operators
  9: {
    content: `Lindiwe sells vegetables at the market. Every morning, she checks competitors' prices.

If her tomatoes cost R25 per kg and the stall next door charges R22, she knows she's more expensive.
If her price is lower, customers come to her first.
If prices are the same, location and quality matter.

She's always comparing: higher, lower, equal.

These comparisons guide her decisions:
If her stock is less than 10kg, reorder.
If a customer's offer is greater than her cost, consider it.
If the total equals the goal, stop for the day.

Programs make the same comparisons constantly.
Greater than. Less than. Equal to. Not equal.

In programming, these are comparison operators—the foundation of all decisions.

Today, you'll learn how to make programs compare values and act on the results.`,
    context: null,
    reflectionPrompt: "When did you compare numbers to make a decision today? What were you comparing? Where could this help you respond to competition and opportunity?",
    teachBackPrompt: "Explain to someone why programs need to compare values. Use an example from buying, selling, or measuring. Why does this kind of thinking matter—knowing whether something is more, less, or equal lets you make informed choices?"
  },

  // Level 10: Nested Conditions
  10: {
    content: `When someone applies for a loan at the community savings group, Thembi checks several things in order.

First: Do they have an ID? If no, stop there. Application denied.

If yes, then check: Are they employed? If no, stop. Denied.

If yes, then check: Is their requested amount less than R5000? If no, denied.

If yes, then finally: Approve.

Each question only matters if the previous answer was yes.
It's not one big decision—it's decisions within decisions.

Programs work the same way when decisions depend on other decisions.
First check one thing. If that passes, then check the next.

In programming, this is called nesting—conditions inside conditions.

Today, you'll learn how to make programs that check one thing, then check another, building complex logic from simple steps.`,
    context: null,
    reflectionPrompt: "Describe a decision you made that had steps within steps—where one answer led to the next question. Where else in your life do you break decisions into layers?",
    teachBackPrompt: "Explain to someone when a program needs to check one thing, then check another inside that. Use an example from approval processes or rules. Why does this kind of thinking matter—building complex logic from simple steps?"
  },

  // Level 11: While Loops
  11: {
    content: `After dinner, Thandiwe washes dishes in a basin of soapy water.

She picks up a plate. Scrubs it. Rinses it. Sets it aside.
Then picks up another. And another.

She doesn't count how many plates there are.
She just keeps washing until the basin is empty.

While there are dirty dishes, keep washing.
When the basin is empty, stop.

This is how much of life works. You don't know exactly how many times you'll repeat something—you just keep going until the condition changes.

While customers are waiting, serve them.
While there's dough left, make more bread.
While the balance is less than the goal, keep saving.

In programming, this is called a while loop. It keeps repeating as long as a condition is true.

Today, you'll learn how to make programs that continue working until something changes.`,
    context: null,
    reflectionPrompt: "What's something you keep doing until it's finished, without counting how many times? Where else in your life do you repeat until a condition changes?",
    teachBackPrompt: "Explain to someone how programs know when to keep doing something and when to stop. Why does this kind of thinking matter—continuing until something is complete, not guessing how many times?"
  },

  // Level 12: For Loops
  12: {
    content: `A delivery truck arrives at Mandla's shop with boxes of supplies.

He needs to check each box: count the items, verify the contents, mark them as received.

Box 1: Check. Mark.
Box 2: Check. Mark.
Box 3: Check. Mark.

He knows there are 12 boxes. He'll check each one, in order, until all are done.

This is different from washing dishes until the basin is empty.
Here, he knows the count. He knows the list. He goes through each item systematically.

Programs do this constantly:
For each name in the list, send a message.
For each item in stock, calculate the total.
For each file in the folder, check the size.

In programming, this is called a for loop. It processes each item in a collection, one by one.

Today, you'll learn how to make programs handle each item in a group.`,
    context: null,
    reflectionPrompt: "When do you go through things one at a time, knowing exactly how many there are? Where else do you process each item methodically?",
    teachBackPrompt: "Explain to someone how programs handle each item in a group, one after another. Why does this kind of thinking matter—processing systematically ensures nothing is missed?"
  },

  // Level 13: Range Function
  13: {
    content: `Nandi bakes bread every morning. Today she needs 20 loaves for orders.

She makes them one at a time:
Loaf 1. Loaf 2. Loaf 3...

She doesn't have a list of loaf names. She just needs to count from 1 to 20.

Each loaf takes the same steps: mix, knead, shape, bake.
She repeats this process exactly 20 times.

Programs often need to do something a specific number of times.
Not processing an existing list, just counting.

Print tickets numbered 1 to 100.
Try connecting to the server 5 times.
Repeat this animation 10 times.

In programming, this uses the range function. It creates a sequence of numbers to count through.

Today, you'll learn how to make programs count from start to end and do something each time.`,
    context: null,
    reflectionPrompt: "When do you need to count a specific number of times? What were you doing? Where else in your life do you repeat something an exact number of times?",
    teachBackPrompt: "Explain to someone how programs count in sequence, from one number to another. Why does this kind of thinking matter—repetition with control?"
  },

  // Level 14: Break and Continue
  14: {
    content: `Sipho is sorting through a crate of tomatoes for his stall.

He picks up each tomato, checks it, and decides:
- If it's good: put it on display.
- If it's slightly bruised: put it in the "discount" box.
- If it's rotten: stop immediately. The whole crate might be bad.

Most times, he checks the whole crate.
But if he finds rot, he doesn't keep sorting—he returns the crate to the supplier.

Sometimes you need to skip one item but keep going.
Sometimes you need to stop the whole process early.

Programs need the same control.
Skip this customer (they already paid) but keep processing others.
Stop searching (we found what we needed).

In programming, "continue" means skip this one, "break" means stop entirely.

Today, you'll learn how to make programs skip or stop when it makes sense.`,
    context: null,
    reflectionPrompt: "When have you stopped a task early because something was wrong? Or skipped one item but continued with others? Where else do you need this kind of control over repetition?",
    teachBackPrompt: "Explain to someone when a program needs to skip one item, or stop entirely before finishing. Why does this kind of thinking matter—knowing when to continue and when to stop saves time and prevents mistakes?"
  },

  // Level 15: Nested Loops
  15: {
    content: `Thabo delivers bread to five shops, six days a week.

Every morning, he makes the same route:
Monday: shop 1, shop 2, shop 3, shop 4, shop 5.
Tuesday: shop 1, shop 2, shop 3, shop 4, shop 5.
And so on.

Six days. Five shops each day. Thirty deliveries total.

It's a repeating task inside a repeating task.
For each day, visit each shop.

Programs encounter this pattern constantly:
For each student, calculate each subject grade.
For each row, check each column.
For each folder, process each file.

In programming, this is called nested loops—loops within loops.

Today, you'll learn how to make programs handle patterns that repeat within other patterns.`,
    context: null,
    reflectionPrompt: "Think of a task that repeats inside another repeating task. Like daily routines, or deliveries. Where else do you handle patterns within patterns?",
    teachBackPrompt: "Explain to someone how programs handle repeating patterns within repeating patterns. Why does this kind of thinking matter—recognizing structure helps you manage complexity?"
  },

  // Level 16: Creating and Accessing Lists
  16: {
    content: `Before leaving for the market, Zanele writes a shopping list:

Bread
Milk
Sugar
Eggs
Soap

She writes it once. Then throughout the market, she checks items off one by one.

She doesn't memorize it—the paper remembers for her.
She doesn't write separate notes—everything is in one place.
She can look at item 3 (sugar) without reading items 1 and 2 again.

Programs use the same concept for storing multiple related items.
Not one variable per item, but one list holding all items.

A list of customer names.
A list of prices.
A list of tasks to complete.

In programming, this is called a list—a collection of items kept together and accessed by position.

Today, you'll learn how to store multiple items in one place and access them when needed.`,
    context: null,
    reflectionPrompt: "What lists do you keep in your life? Why is it easier than remembering everything? What did the computer do exactly as instructed when organizing your list?",
    teachBackPrompt: "Explain to someone why it's useful to group related items together instead of keeping them separate. Why does this kind of thinking matter—storing multiple items in one place helps you track and access information efficiently?"
  },

  // Level 17: List Methods
  17: {
    content: `Nomvula keeps a task list on her phone for the day:

Morning: "Buy vegetables" "Pay rent" "Call Mama"

Midday, her boss adds a task: "Deliver parcel to Mrs. Khumalo"
She adds it to the list.

Afternoon, she finishes "Buy vegetables"
She removes it from the list.

Evening, she realizes she forgot to add "Pick up kids at 4pm"
She inserts it in the middle where it belongs by time.

Her list changes throughout the day. It's not written in stone.
Items get added. Items get removed. Items get rearranged.

Programs work with lists the same way.
Add a new customer.
Remove a sold-out item.
Update a price.

In programming, these are called list methods—actions you can take on a list.

Today, you'll learn how to add, remove, and change items in lists.`,
    context: null,
    reflectionPrompt: "How do you update your lists when things change during the day? What did the computer do exactly as instructed when you modified the list?",
    teachBackPrompt: "Explain to someone how programs add and remove items from lists as things change. Why does this kind of thinking matter—lists that can adapt as reality changes?"
  },

  // Level 18: List Slicing
  18: {
    content: `At the taxi rank, a queue of 20 people waits.

When a taxi arrives, it takes the first 7 people.
Not everyone. Not random people. The first 7 in line.

The driver doesn't need to look at positions 8 through 20.
Just: "First seven, climb in."

Programs often need a portion of a list, not the whole thing.
The first 5 customers.
The last 10 transactions.
Items 3 through 7.

You don't want to process every single item when you only need part of the collection.

In programming, this is called slicing—taking a portion of a list by specifying the start and end positions.

Today, you'll learn how to work with specific portions of lists.`,
    context: null,
    reflectionPrompt: "When do you need just part of a list, not all of it? Like the first few people, or the last items? Where else do you work with portions instead of everything?",
    teachBackPrompt: "Explain to someone how programs work with portions of collections instead of always using everything. Why does this kind of thinking matter—focusing on what's needed saves time and effort?"
  },

  // Level 19: List Comprehensions
  19: {
    content: `It's Friday, and the clothing shop is running a 20% off sale on everything.

The owner, Precious, has a price list with 50 items.
She needs new sale prices for all of them.

She could:
Take item 1, calculate 80% of the price, write it down.
Take item 2, calculate 80% of the price, write it down.
Repeat 48 more times.

Or she could:
"Take every price and multiply by 0.8."
One instruction. All items processed.

When you need to do the same thing to every item in a list, programs can do it in one efficient step.

Not a loop that writes one line at a time.
A single instruction that transforms the entire list.

In programming, this is called a list comprehension—applying the same operation to every item efficiently.

Today, you'll learn how to transform entire lists in one step.`,
    context: null,
    reflectionPrompt: "When have you needed to change all items in the same way? Like applying a discount, or doubling quantities? Where could this help you save time?",
    teachBackPrompt: "Explain to someone how programs can process whole collections efficiently instead of one item at a time. Why does this kind of thinking matter—one instruction for many items is powerful?"
  },

  // Level 20: Iterating Over Lists
  20: {
    content: `The teacher, Mr. Mthembu, calls attendance every morning.

He has a register with 35 names.
He goes through it systematically:

"Andile?" - "Present."
"Buhle?" - "Present."
"Cebo?" - "Absent."

One by one, in order, until the list is complete.

He doesn't skip names. He doesn't go backwards. He doesn't pick randomly.
He processes each name, in sequence, doing the same action—checking if they're there.

Programs do this constantly with collections:
For each email address, send a message.
For each product, check if it's in stock.
For each transaction, verify the amount.

In programming, this is called iterating—going through a list item by item, doing something with each one.

Today, you'll learn how to process every item in a list systematically.`,
    context: null,
    reflectionPrompt: "When do you go through a list doing the same thing to each item? Like checking names, or packing orders? Where else do you process each item methodically?",
    teachBackPrompt: "Explain to someone how programs handle each item in a list in order, doing the same task for each. Why does this kind of thinking matter—one at a time, in sequence, ensures consistency and completeness?"
  },

  // Level 21: Tuples
  21: {
    content: `Sibusiso records GPS coordinates for community garden plots.

Plot 7: -29.8587, 31.0218
Plot 8: -29.8601, 31.0229

These two numbers always go together. Latitude and longitude.
You never change just one. If the location is wrong, you record both numbers again—you don't edit them separately.

They're paired. Fixed. Protected.

Programs often have information that should stay together and shouldn't be modified.
A date: (year, month, day) - you don't change one part.
RGB color: (255, 100, 50) - all three values define the color.
Coordinates: (x, y) - they're meaningless apart.

In programming, this is called a tuple—a collection that groups related values and keeps them unchanged.

Today, you'll learn when to use fixed groups instead of changeable lists.`,
    context: null,
    reflectionPrompt: "What information stays together but shouldn't be changed? Like ID numbers, or coordinates? What did the computer do exactly as instructed when protecting this data?",
    teachBackPrompt: "Explain to someone when you want information grouped but protected from changes. Why does this kind of thinking matter—some information should stay together and unchanged?"
  },

  // Level 22: Dictionaries (Basics)
  22: {
    content: `Nothando keeps a small notebook with important contacts:

Mama: 073 555 1234
Doctor: 011 555 9876
Landlord: 082 555 4321

She doesn't memorize phone numbers. She looks them up by name.

When she needs to call the doctor, she doesn't flip through every page.
She finds "Doctor" and reads the number next to it.

Name → Number. Direct connection.

Programs use the same structure constantly:
Username → Password
Product code → Price
Student ID → Grade

You look something up by its name or label, not by counting through a list.

In programming, this is called a dictionary—a collection that connects labels (keys) to values.

Today, you'll learn how to create lookups where you find information by name instead of position.`,
    context: null,
    reflectionPrompt: "When do you look things up using a name or label instead of counting through a list? Where else in your life do you use direct lookups instead of searching?",
    teachBackPrompt: "Explain to someone how programs connect labels to information, like names to phone numbers. Why does this kind of thinking matter—direct lookups save time and effort?"
  },

  // Level 23: Dictionary Methods
  23: {
    content: `The shop's price list changes weekly. Vusi keeps it on a laminated sheet he can update.

Monday: Bread R12
Wednesday: Bread price increases to R14. He crosses out R12, writes R14.
Friday: Sugar added to stock. He adds: Sugar R18
Saturday: Eggs sold out. He erases that line.

The list isn't static. Prices change. Items get added. Items get removed.

But he still looks things up the same way:
Customer asks: "How much is bread?"
He finds "Bread" and reads the current price.

Programs manage lookup information the same way.
Add new entries.
Update existing values.
Remove items that are no longer relevant.
Check if something exists before using it.

In programming, these are dictionary methods—ways to work with key-value pairs.

Today, you'll learn how to update, add, remove, and check information in lookups.`,
    context: null,
    reflectionPrompt: "How do you update information when prices or details change? Do you rewrite everything or just update what changed? Where else do you need to adapt information as things change?",
    teachBackPrompt: "Explain to someone how programs manage information that's looked up by name when it changes over time. Why does this kind of thinking matter—adapting to change while keeping connections intact?"
  },

  // Level 24: Sets
  24: {
    content: `The community hall keeps a VIP list for events. Only people on the list get early entry.

The security guard has the list:
Thandi Dlamini
Bongani Khumalo
Mandla Ntuli

When someone arrives, he checks: "Are you on the list?"
Either yes or no. That's all that matters.

He doesn't care how many times their name appears.
He doesn't care about order.
He just needs to know: is this person on the list or not?

If someone tries to add "Thandi Dlamini" twice, the list doesn't grow—she's already there.

Programs use this structure when they need unique items only.
List of visited websites (no duplicates).
Set of logged-in users (each person once).
Collection of unique product codes.

In programming, this is called a set—a collection that automatically prevents duplicates.

Today, you'll learn when to use collections that keep only unique items.`,
    context: null,
    reflectionPrompt: "When do you need a list with no repeated items? Like attendance, or membership? Where else does uniqueness matter?",
    teachBackPrompt: "Explain to someone why programs sometimes need collections with no duplicates. Why does this kind of thinking matter—uniqueness ensures accuracy?"
  },

  // Level 25: Choosing the Right Collection
  25: {
    content: `Nonhlanhla runs a small stationery business. She organizes information in different ways.

Customer orders: She uses a numbered list.
Order 1, Order 2, Order 3. Position matters.

Price catalog: She uses a lookup.
"Pen" → R5. "Notebook" → R25. She finds items by name.

GPS coordinates for deliveries: She uses paired numbers that don't change.
(-26.2041, 28.0473) - always together, never edited separately.

Loyalty program members: She uses a membership list with no duplicates.
Each person appears once, no matter how many times they shop.

Four different purposes. Four different structures.
The choice depends on what the information needs to do.

Programs make the same decisions.
Use a list when order matters and duplicates are okay.
Use a dictionary when you look things up by name.
Use a tuple when values are paired and fixed.
Use a set when you need unique items only.

Today, you'll learn how to choose the right structure for your information.`,
    context: null,
    reflectionPrompt: "How do you decide how to organize information? What makes you use a list vs. a lookup vs. something else? Where else do you choose different structures for different needs?",
    teachBackPrompt: "Explain to someone how different ways of organizing information help with different tasks. Why does this kind of thinking matter—choosing the right structure solves problems efficiently?"
  },

  // Level 26: Defining Functions
  26: {
    content: `Every morning, Zodwa's mother makes pap the same way.

Measure 2 cups of maize meal.
Boil 4 cups of water.
Stir in the meal slowly.
Cook for 15 minutes, stirring often.

She doesn't relearn the recipe every day.
She wrote it down once, and now she just follows it.

If Zodwa's brother wants pap, same recipe.
If a neighbor asks how to make it, she shares the same instructions.

One recipe, used many times, by different people, with the same result.

Programs work the same way.
You write the instructions once, give them a name, and reuse them whenever needed.

This is called a function—a block of code that does one clear task, written once, used many times.

Today, you'll learn how to write instructions that can be reused, saving effort and preventing mistakes.`,
    context: null,
    reflectionPrompt: "What's something you do the exact same way every time? Why don't you need to rethink it each time? Where else in your life do you have repeatable processes?",
    teachBackPrompt: "Explain to someone why it helps to write instructions once and reuse them. Use an example from your life. Why does this kind of thinking matter—reusable processes save effort and reduce mistakes?"
  },

  // Level 27: Parameters and Arguments
  27: {
    content: `Samuel drives a taxi. He calculates fares using the same formula every time:

Base fare (R8) + (distance in km × R3)

The formula never changes. But the distance is different for each trip.

Trip to town: 5km → R8 + (5 × R3) = R23
Trip to market: 3km → R8 + (3 × R3) = R17
Trip to hospital: 8km → R8 + (8 × R3) = R32

Same calculation process. Different input each time.

Programs work the same way with functions.
The instructions stay the same, but you can give them different information to work with.

Calculate tax (for this amount).
Send message (to this phone number).
Find customer (with this name).

In programming, parameters are the placeholders in the function. Arguments are the actual values you provide.

Today, you'll learn how to make functions flexible by giving them different information to work with.`,
    context: null,
    reflectionPrompt: "When do you follow the same process but with different details each time? Like cooking for different numbers of people? Where else do you adapt the same process to different situations?",
    teachBackPrompt: "Explain to someone how programs use the same instructions with different information. Why does this kind of thinking matter—flexibility through parameters?"
  },

  // Level 28: Return Values
  28: {
    content: `When Lerato goes to the ATM, she follows steps:

Insert card.
Enter PIN.
Select "Check balance."
The ATM shows: R1,247.50

The machine doesn't just do the work and stay silent.
It gives her back the information she needs.

She asked a question. The machine calculated. The machine answered.
Now she can use that answer to decide what to do next.

Programs work the same way with functions.
You ask a function to calculate something.
The function does the work.
The function gives back the result.
You use that result for the next step.

Without getting the result back, the calculation is useless.

In programming, this is called a return value—the answer a function gives back after doing its work.

Today, you'll learn how to make functions give back results you can use.`,
    context: null,
    reflectionPrompt: "When do you need an answer from a process to use in the next step? Like asking a price before deciding to buy? Where else do steps depend on previous results?",
    teachBackPrompt: "Explain to someone why programs need to receive answers back from functions. Why does this kind of thinking matter—information flows between steps?"
  },

  // Level 29: Scope
  29: {
    content: `In the kitchen, Ntombi is making two dishes at once.

For the curry, she has spices on her left.
For the salad, she has dressing on her right.

The curry spices stay with the curry. They don't mix into the salad.
The dressing stays with the salad. It doesn't go into the curry.

But the salt in the middle? Both dishes can use it. It's shared.

Some ingredients are private to one dish.
Some ingredients are shared by everything.

Programs organize information the same way.
Variables inside a function are private—only that function can see them.
Variables outside, at the top level, are shared—everyone can see them.

This prevents mistakes. The curry's spice level doesn't accidentally affect the salad.

In programming, this is called scope—where a variable is visible and can be used.

Today, you'll learn how programs keep information private or shared.`,
    context: null,
    reflectionPrompt: "When do you use something just for one task vs. shared across tasks? Like tools, or ingredients? Where else do you manage what's private vs. what's shared?",
    teachBackPrompt: "Explain to someone when information should be private to one function vs. shared across a program. Why does this kind of thinking matter—boundaries protect against mistakes?"
  },

  // Level 30: Built-in Functions
  30: {
    content: `When Musa needs to add numbers, he uses the calculator app on his phone.

He doesn't build a calculator from scratch.
He doesn't write the addition code himself.
Someone already made it. He just uses it.

The same when he needs to know the time—he checks the clock.
When he needs to send a message—he uses the messaging app.

Tools that already exist. Built by others. Ready to use.

Python comes with the same kind of ready-made tools.
len() tells you how long a list is.
max() finds the biggest number.
sum() adds numbers together.
input() asks the user for information.

You don't need to write these from scratch.
Someone already built them. You just use them.

In programming, these are called built-in functions—tools that come with Python, ready to use.

Today, you'll learn how to use Python's built-in tools instead of reinventing them.`,
    context: null,
    reflectionPrompt: "When do you use a tool someone else made instead of building your own? Why? Where else do you leverage existing solutions?",
    teachBackPrompt: "Explain to someone why programs come with ready-made tools and when to use them. Why does this kind of thinking matter—leveraging existing solutions is efficient?"
  },

  // Level 31: String Methods
  31: {
    content: `Thandiwe works at a clinic. People sign in by hand, writing their names.

Some write in ALL CAPS: "LINDA NKOSI"
Some write in lowercase: "linda nkosi"
Some add extra spaces: "  Linda  Nkosi  "

When she enters names into the computer system, they need to be consistent:
"Linda Nkosi"

She doesn't retype everything from scratch.
She takes what's there and cleans it:
Make it title case.
Remove extra spaces.

Programs handle text the same way.
Customer entered email in CAPS? Convert to lowercase.
User typed extra spaces? Strip them.
Need to check if text contains a word? Search it.
Need to replace old information? Substitute it.

In programming, these are string methods—tools for working with and cleaning up text.

Today, you'll learn how to manipulate text programmatically.`,
    context: null,
    reflectionPrompt: "When do you need to change how text looks or is formatted? Like names, addresses, or phone numbers? What did the computer do exactly as instructed when manipulating text?",
    teachBackPrompt: "Explain to someone how programs work with and clean up text. Why does this kind of thinking matter—messy data needs cleaning before use?"
  },

  // Level 32: Modules and Imports
  32: {
    content: `Bongani is fixing a broken chair. He needs a specific tool—a socket wrench.

He doesn't own one. But his neighbor does.

He walks next door: "Can I borrow your socket wrench?"
His neighbor hands it over.
He uses it. Returns it when done.

He didn't need to buy the tool. He just needed access when required.

Programs work the same way with code.
Someone else wrote a date calculator. You don't need to write it again—you borrow it.
Someone else wrote an image processor. You don't rebuild it—you use theirs.

In Python, these pre-written tools are called modules.
To use them, you import them—bring them into your program temporarily.

math module: advanced calculations.
datetime module: working with dates and times.
random module: generating random numbers.

Today, you'll learn how to use code that others have written, saving you time and effort.`,
    context: null,
    reflectionPrompt: "When do you use knowledge or tools from others instead of figuring everything out yourself? Where else do you build on others' work?",
    teachBackPrompt: "Explain to someone how programs share and use each other's tools. Why does this kind of thinking matters—building on others' work accelerates progress?"
  },

  // Level 33: Reading Files
  33: {
    content: `At the end of each day, Sipho writes the day's sales in a ledger book.

Morning: R450
Afternoon: R780
Evening: R620

The next morning, he opens the book to see yesterday's total.

He doesn't remember the exact numbers. He doesn't need to.
The book remembers for him.

Programs work the same way with files.
Information written yesterday (or last week, or last year) can be opened and read today.

Customer list saved in a file? Open it and read the names.
Prices saved yesterday? Read them back.
Messages someone sent? Read them from storage.

The program didn't need to be running the whole time.
The file preserved the information.

In programming, this is reading files—loading information from permanent storage.

Today, you'll learn how to make programs read information they saved before.`,
    context: null,
    reflectionPrompt: "When do you need to look at information you saved before? Like receipts, records, or notes? What did the computer do exactly as instructed when reading stored data?",
    teachBackPrompt: "Explain to someone how programs remember information between uses, even after turning off. Why does this kind of thinking matter—persistence enables continuity?"
  },

  // Level 34: Writing Files
  34: {
    content: `Every evening before closing, Nomsa records the day's cash register totals in a notebook.

Not on loose paper that might get lost.
Not in her memory, which might forget.
In the permanent record book.

Tomorrow, next week, next month—she can look back and see what happened.

Programs need the same permanence.

User created a document? Save it to a file so it's there tomorrow.
Program calculated monthly totals? Write them to a file.
System recorded an error? Log it for later review.

Without writing to files, everything disappears when the program closes.
With files, information survives.

In programming, this is writing files—saving information permanently so it can be used later.

Today, you'll learn how to make programs save information they need to keep.`,
    context: null,
    reflectionPrompt: "Why do you write things down instead of just remembering? What would happen if you didn't? What did the computer do exactly as instructed when saving data?",
    teachBackPrompt: "Explain to someone how programs save information they need to keep, even after stopping. Why does this kind of thinking matter—data that disappears is useless?"
  },

  // Level 35: Working with CSV
  35: {
    content: `Thando manages inventory for a hardware store. She keeps a spreadsheet:

| Item      | Price | Stock |
|-----------|-------|-------|
| Nails     | R45   | 120   |
| Hammer    | R180  | 8     |
| Paint     | R95   | 15    |

Rows are items. Columns are details.

She can sort by price. Filter by stock. Add new rows. Update values.

Programs work with this kind of table-structured information constantly.
Customer database: each row is a person, columns are name, phone, email.
Sales records: each row is a transaction, columns are date, amount, customer.
Student grades: each row is a student, columns are subjects.

CSV files store this information in a simple format that both humans and programs can read.

In programming, working with CSV means reading tables, updating rows, adding data, and saving changes.

Today, you'll learn how to handle table-structured information in programs.`,
    context: null,
    reflectionPrompt: "When do you organize information in rows and columns? Like lists, registers, or records? What did the computer do exactly as instructed when handling structured data?",
    teachBackPrompt: "Explain to someone how programs work with table-like information, with rows and columns. Why does this kind of thinking matter—structured data enables analysis?"
  },

  // Level 36: Exception Basics (Try/Except)
  36: {
    content: `At the till, Zanele processes payments all day.

Usually, everything works:
Customer pays with card. Transaction approved. Receipt printed.

But sometimes things go wrong:
Card is declined. Network is down. Machine jams.

When that happens, she doesn't just freeze.
She has a backup plan: "I'm sorry, the card didn't work. Can you try again or pay with cash?"

The process doesn't crash. She handles the problem gracefully and continues.

Programs encounter the same issues.
File doesn't exist. Network fails. User enters invalid data.

Without a plan, the program crashes.
With a plan, it handles the error and keeps running.

In programming, this is called exception handling—preparing for things that might go wrong.

Today, you'll learn how to make programs handle problems gracefully instead of crashing.`,
    context: null,
    reflectionPrompt: "When have you needed a backup plan because something failed? What did you do instead? Where else in your life do you prepare for things that might go wrong?",
    teachBackPrompt: "Explain to someone why programs need to handle problems gracefully instead of just stopping. Why does this kind of thinking matter—resilience keeps systems running?"
  },

  // Level 37: Specific Exceptions
  37: {
    content: `Mandla runs a delivery service. Different problems need different responses.

Customer not home? → Leave note, return tomorrow.
Wrong address? → Call customer for correct address.
Road closed? → Find alternate route.

Same outcome (delivery didn't happen), but different solutions depending on why.

Programs make the same distinctions.

File not found? → Create it or tell the user.
Division by zero? → Return error message, don't crash.
Network timeout? → Retry automatically.
Invalid input? → Ask the user to correct it.

Not all errors are the same. The response should match the problem.

In programming, these are specific exceptions—recognizing the type of error and responding appropriately.

Today, you'll learn how to handle different errors in different ways.`,
    context: null,
    reflectionPrompt: "How do you respond differently to different types of problems? Like being late vs. forgetting vs. breaking something? Where else do different situations require different responses?",
    teachBackPrompt: "Explain to someone why programs handle different errors in different ways. Why does this kind of thinking matter—different problems need different solutions?"
  },

  // Level 38: Debugging Strategies
  38: {
    content: `At the end of the day, the till doesn't balance. There should be R2,450. There's R2,350.

Nonhlanhla doesn't panic. She traces backwards systematically:

Check the closing count. Correct.
Check the receipts. All there.
Check the opening float. Wait—she started with R50, not R150.
Found it. The difference is R100. That's where the error came from.

She didn't guess. She didn't assume.
She checked each step until she found where things went wrong.

Programs break the same way. And fixing them requires the same process.

Where did the error occur?
What was the expected result?
What actually happened?
What's different?

In programming, this is called debugging—systematically finding and fixing what went wrong.

Today, you'll learn how to trace problems back to their source.`,
    context: null,
    reflectionPrompt: "How do you figure out what went wrong when something breaks? Do you guess, or trace backwards? Where else do you investigate systematically?",
    teachBackPrompt: "Explain to someone how you trace problems back to their source instead of guessing. Why does this kind of thinking matter—systematic investigation finds root causes?"
  },

  // Level 39: Final Project - Part 1 (Planning & Structure)
  39: {
    content: `Thembi wants to start tracking her small catering business properly.

She needs:
- A list of dishes she offers, with prices.
- A way to record orders from customers.
- A way to calculate daily totals.
- A way to see which dishes sell most.

She doesn't try to build everything at once. She breaks it down:

Step 1: Create the menu (list of dishes and prices).
Step 2: Record one order (customer name, dish, quantity).
Step 3: Calculate the total for that order.
Step 4: Save the order to a file.
Step 5: Later, read all orders and summarize.

Small pieces. Clear order. One step at a time.

Programs are built the same way.
Not all at once, but piece by piece, testing as you go.

In programming, this is planning and structure—breaking a real problem into manageable parts.

Today, you'll plan a complete system before writing all the code.`,
    context: null,
    reflectionPrompt: "How do you approach a large task you've never done before? Do you start anywhere, or plan first? Where else in your life do you break down complex work into manageable parts?",
    teachBackPrompt: "Explain to someone how you organize work that's too big to do all at once. Why does this kind of thinking matter—breaking down complexity makes it manageable?"
  },

  // Level 40: Final Project - Part 2 (Integration & Testing)
  40: {
    content: `Thembi built the pieces of her catering system. Now she puts them together.

The menu exists.
Orders can be recorded.
Totals calculate correctly.
Files save and load.

But does it actually work as a complete system?

She tests it like a real day:
Morning: Add today's specials to the menu.
Midday: Record three orders from customers.
Afternoon: Check totals. Verify the numbers.
Evening: Save everything. Close the system.
Next morning: Reopen. Confirm yesterday's data is still there.

Only when she's tested the full flow does she trust it.

Programs require the same verification.
The pieces might work alone, but do they work together?

In programming, this is integration and testing—combining parts and verifying the whole system functions correctly.

Today, you'll complete, integrate, and test your full system.`,
    context: null,
    reflectionPrompt: "How do you know if a system actually works? Do you assume, or do you test it? Where else do you verify instead of assuming?",
    teachBackPrompt: "Explain to someone how you know your work is complete and correct, not just finished. Why does this kind of thinking matter—testing validates assumptions?"
  },

  // Level 41: Understanding AI Outputs (AI Literacy - No Code)
  41: {
    content: `Thabo runs a small shop. One morning, a well-dressed stranger walks in with confidence.

"I'm from the municipality," he says. "You owe R2,500 in back fees. Pay now or we close you down tomorrow."

The stranger sounds certain. He has papers. He knows the shop's name. He speaks with authority.

Thabo feels panic rising. Then he remembers: he paid all fees last month. He has the receipt.

He checks. The stranger's papers are fake. The "official stamp" is printed from a home printer.

The man sounded confident. He had details. But he was wrong—and lying.

---

This is how AI systems work today.

They generate text that sounds confident, professional, and detailed.
But confidence is not the same as correctness.
Details are not the same as truth.

An AI can write: "The population of Johannesburg in 2025 is 6.2 million people."
It sounds precise. It might be completely wrong.

An AI can say: "To fix your car's engine, disconnect the fuel pump first."
It sounds knowledgeable. It might destroy your car.

Why? Because AI systems don't know things. They predict words.

They've read millions of texts. They learned patterns:
- After "population of," numbers often follow.
- After "fix your," instructions often follow.

But they don't check facts. They don't verify. They don't say "I don't know."

They generate what sounds right based on patterns, not what is right based on reality.

---

This matters in your life right now.

Students use AI to write essays. The essays sound smart but contain fake citations.
Job seekers use AI to write applications. The applications sound professional but claim skills they don't have.
People use AI for medical advice. The advice sounds informed but can be dangerous.

The AI isn't trying to lie. It's doing exactly what it was built to do: predict likely words.

But likely words ≠ true words.

---

As humans, we have a responsibility.

When you use AI:
- You are responsible for checking what it generates.
- You are responsible for the consequences if it's wrong.
- You are responsible for understanding it's predicting patterns, not stating facts.

If you submit AI-generated work without verifying it, the mistake is yours.
If you follow AI advice without confirming it, the risk is yours.
If you trust AI output because it sounds confident, the error is yours.

---

The power of AI is real. The risk of AI is also real.

You now understand:
- Conditionals (if this, then that)
- Logic (and, or, not)
- Data types (text, numbers, yes/no)
- Comparison (greater, less, equal)

You know how computers follow instructions exactly, without interpretation.
You know how programs make decisions based on conditions, not feelings.

AI systems make patterns look like thinking.
But you know the difference now.

When an AI generates text, ask:
- Is this factually correct, or just well-phrased?
- Can I verify this independently?
- What happens if this is wrong?

This is AI Literacy: not fearing machines, but understanding them clearly enough to use them responsibly.`,
    context: null,
    reflectionPrompt: "When has someone sounded very confident about something that turned out to be wrong? How did you figure out the truth? What does this teach you about AI-generated content?",
    teachBackPrompt: "Explain to someone younger why a machine can sound confident even when it's wrong. Use an example from your own life. Why does this kind of thinking matter—distinguishing confidence from correctness?"
  }
};

/**
 * Get story for a specific level
 */
export function getStory(levelId) {
  return stories[levelId] || null;
}

/**
 * Check if a level has a real story (not just a placeholder)
 */
export function hasRealStory(levelId) {
  const story = stories[levelId];
  if (!story) return false;

  // Check if content starts with [STORY NEEDED]
  return !story.content.startsWith('[STORY NEEDED');
}
